/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { GridComponent } from '@src/app/shared/grid/grid.component';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { ActionGridField } from '@src/app/shared/action/action-grid-field';
import { ActionType } from '@src/app/shared/action/add-action-form-action';

/**
 * The simple action list's "expand" control opens this: the same actions in the full
 * grid, in a large window, with the same two add buttons the inline views use.
 */
@Component({
    selector: 'app-action-list-dialog',
    imports: [MatDialogModule, MatButtonModule, MatTooltipModule, DialogBannerComponent, GridComponent],
    template: `
        <app-dialog-banner text="Actions" [dialogRef]="dialogRef"></app-dialog-banner>
        <mat-dialog-content class="body">
            <app-grid [control]="data.control" on="form"></app-grid>
            @if (!data.control.field.readonly) {
                <div class="grid-add">
                    <button type="button" mat-raised-button color="primary" data-grid-new="reminder"
                            matTooltip="Add a reminder to do something in the future"
                            (click)="addAction('future')">Add reminder</button>
                    <button type="button" mat-raised-button color="primary" data-grid-new="action"
                            matTooltip="Log an action that has already been completed"
                            (click)="addAction('past')">Log completed</button>
                </div>
            }
        </mat-dialog-content>
    `,
    styles: [`
        .body {
            width: 80vw;
            max-width: 1100px;
            min-height: 50vh;
            padding-top: 12px;
        }
        .grid-add {
            display: flex;
            justify-content: center;
            gap: 12px;
            margin-top: 10px;
        }
        .grid-add button {
            font-size: small;
            height: 24px;
        }
    `]
})
export class ActionListDialogComponent {
    dialogRef = inject<MatDialogRef<ActionListDialogComponent>>(MatDialogRef);
    data = inject<{ control: GridControl }>(MAT_DIALOG_DATA);

    addAction(actionType: ActionType): void {
        const field = this.data.control.field as ActionGridField;
        field.upsertAction(null, actionType).subscribe(created => {
            if (created && created !== 1) {
                this.data.control.addRow(created, true, true);
            }
        });
    }
}
